import { Container } from "pixi.js";
import { EventTimeline, Spine, type SkeletonData, type TrackEntry } from "@esotericsoftware/spine-pixi-v8";
import type { CannonBulletView } from "./CannonBulletView";
import { CANNON_TUNING } from "./cannonTuning";
import { hideSlotAttachmentsByPrefix, socketContainerToBone } from "@slot/presentation";
import type { PresentationTaskRunner, SpineDriver } from "@slot/presentation";

/** `HudLayer/CannonPanel/root/cannonNode` + `cannon_top` (scene dump): position (stored y-down) and scale. */
const CANNON_PLACEMENT = { x: -390.78, y: 459.27, scale: 0.282 } as const;

/**
 * The blast wait's safety net, as a multiple of the fire event's authored time: the fallback fires only
 * when the event is genuinely lost, never in the ordinary frame-to-frame race. A shot clip keeps the time
 * scale captured when it started, so a speed change mid-shot moves the event later than its authored time.
 */
const FIRE_IMPACT_TIMEOUT_SAFETY = 2;

/**
 * CannonView — the Cannon (win-multiplier-with-queue) scene view: the `cannon_bottom` body Spine
 * (bore + queue + muzzle fire) and the `cannon_top` overlay (outer barrel) played in LOCKSTEP, the
 * chamber + queue rounds SOCKETED to the body's placeholder bones so they ride idle/load/shot, and
 * the round-load z-order flip (`roundLoadPlaceholder`, after cannon_top). No game logic / money —
 * `CannonPresenter` drives it from the server's `apply_win_multiplier` state.
 *
 * Tree (from the scene dump): CannonPanel/root > cannonNode (Spine, −390.8,−459.3 y-up, ×0.282) >
 * cannon_top (same placement) > roundLoadPlaceholder. Both Spines are ticked by the `SpineDriver`.
 */
export class CannonView extends Container {
  public readonly cannonSkeleton: Spine | null;
  public readonly cannonSkeletonTop: Spine | null;
  /** Front z-order slot for the loading round — after cannon_top, so its child renders in front. */
  public readonly roundLoadPlaceholder: Container;

  private runner: PresentationTaskRunner | null = null;
  private createBullet: (() => CannonBulletView) | null = null;
  private queueShine: ((bulletRoot: Container) => void) | null = null;
  /** Chamber + queue bullets (index 0 = chamber), socketed to the body's placeholder bones. Built once. */
  private readonly queueBullets: CannonBulletView[] = [];
  /** The socket followers the bullets hang under (index-aligned with `queueBullets`). */
  private readonly queueFollowers: Container[] = [];
  private loadingRound: CannonBulletView | null = null;
  /** Set while a fire waits for the blast; the Spine listener calls it. */
  private resolveFireImpact: (() => void) | null = null;
  /** The blast wait's fallback, read once from the shot clip. */
  private fireImpactTimeout: number | null = null;
  private lastChamberMultiplier = "";
  private appliedLoadout: string[] = [];
  private pendingLoadout: string[] | null = null;

  public constructor(input: {
    skeletonData: SkeletonData | null;
    topSkeletonData: SkeletonData | null;
    spineDriver: SpineDriver;
  }) {
    super({ label: "CannonPanel" });
    const spawn = (data: SkeletonData | null, label: string): Spine | null => {
      if (!data) return null;
      const spine = new Spine({ skeletonData: data, autoUpdate: false });
      spine.label = label;
      spine.position.set(CANNON_PLACEMENT.x, CANNON_PLACEMENT.y);
      spine.scale.set(CANNON_PLACEMENT.scale);
      input.spineDriver.attach(spine);
      this.addChild(spine);
      return spine;
    };
    this.cannonSkeleton = spawn(input.skeletonData, "cannonNode");
    this.cannonSkeletonTop = spawn(input.topSkeletonData, "cannon_top");
    this.roundLoadPlaceholder = new Container({ label: "roundLoadPlaceholder" });
    this.addChild(this.roundLoadPlaceholder);
  }

  public configure(input: {
    runner: PresentationTaskRunner;
    /** Builds one round (the `cannonProjectile` prefab). Null → the queue simply doesn't show. */
    createBullet: (() => CannonBulletView) | null;
    /** Optional — glint a round's sprites (plate + digits) on a silent loadout replace. */
    queueShine?: ((bulletRoot: Container) => void) | null;
  }): void {
    this.runner = input.runner;
    this.createBullet = input.createBullet;
    this.queueShine = input.queueShine ?? null;
    this.hideRoundGuideSlots();
    this.applyClipMixes();
    this.startIdle();
    this.bindQueueSockets();
    // Drive the round-load z-order flip off the `load` clip STARTING (the only `load` that plays is
    // the one queued after a shot) and apply the staged post-fire VALUES at that moment.
    this.cannonSkeleton?.state.addListener({
      start: (entry: TrackEntry) => {
        if (entry.animation?.name !== CANNON_TUNING.spine.loadClip) return;
        if (this.pendingLoadout) {
          this.applyLoadoutValues(this.pendingLoadout);
          this.pendingLoadout = null;
        }
        this.presentRoundLoad(this.lastChamberMultiplier, entry.animation.duration);
      },
      // The blast: the shot clip's own event ends the fire wait, so the number bursts on the frame the
      // round leaves the muzzle whatever the clip's timing is.
      event: (entry, event) => {
        if (entry.animation?.name === CANNON_TUNING.spine.shotClip && event.data.name === CANNON_TUNING.spine.fireEventName) this.resolveFireImpact?.();
      },
      // A shot that is interrupted (the next spin's idle, a recovery) or ends without its event still
      // releases the wait, so a pending impact never outlives its clip.
      interrupt: (entry) => {
        if (entry.animation?.name === CANNON_TUNING.spine.shotClip) this.resolveFireImpact?.();
      },
      end: (entry) => {
        if (entry.animation?.name === CANNON_TUNING.spine.shotClip) this.resolveFireImpact?.();
      },
    });
  }

  /** Build the chamber + queue ONCE: per placeholder bone, socket a follower + hang a scaled bullet. */
  private bindQueueSockets(): void {
    const skeleton = this.cannonSkeleton;
    if (!skeleton || !this.createBullet || this.queueBullets.length > 0) return;
    const scale = CANNON_TUNING.queueBulletScale;
    for (const { bone, slot } of CANNON_TUNING.spine.placeholders) {
      const bullet = this.createBullet();
      // The round's alpha rides its guide slot: the clip's own fade takes the fired round out on the
      // blast and brings the newly loaded one in, with no timing repeated here.
      const follower = socketContainerToBone(skeleton, bone, bullet, scale, { alphaFromSlot: slot });
      if (!follower) {
        bullet.destroy({ children: true });
        continue;
      }
      bullet.visible = false;
      this.queueBullets.push(bullet);
      this.queueFollowers.push(follower);
    }
  }

  /** Hide EVERY spine-drawn round guide slot (by prefix) on both layers — visible rounds are exclusively the bullets. */
  private hideRoundGuideSlots(): void {
    for (const sk of this.bodySkeletons()) hideSlotAttachmentsByPrefix(sk, CANNON_TUNING.spine.guideSlotPrefix);
  }

  /** Crossfade the body clips into each other; `shot → load` stays a HARD CUT. */
  private applyClipMixes(): void {
    const mix = CANNON_TUNING.spine.mixSeconds;
    if (mix <= 0) return;
    const { idleClip, loadClip, shotClip } = CANNON_TUNING.spine;
    const clips = [idleClip, loadClip, shotClip];
    for (const sk of this.bodySkeletons()) {
      for (const from of clips) {
        for (const to of clips) {
          if (from === to) continue;
          if (from === shotClip && to === loadClip) continue;
          if (sk.skeleton.data.findAnimation(from) && sk.skeleton.data.findAnimation(to)) sk.state.data.setMix(from, to, mix);
        }
      }
    }
  }

  private bodySkeletons(): Spine[] {
    const out: Spine[] = [];
    if (this.cannonSkeleton) out.push(this.cannonSkeleton);
    if (this.cannonSkeletonTop) out.push(this.cannonSkeletonTop);
    return out;
  }

  private startIdle(): void {
    if (!this.runner) return;
    const ts = this.runner.getTimeScale();
    for (const sk of this.bodySkeletons()) {
      sk.state.timeScale = ts;
      sk.state.setAnimation(0, CANNON_TUNING.spine.idleClip, true);
      sk.update(0);
    }
  }

  /** `shot` → `load` (queued after the settle) → `idle`, in lockstep on both layers. */
  private playShotThenReload(): boolean {
    if (!this.runner) return false;
    const ts = this.runner.getTimeScale();
    let played = false;
    for (const sk of this.bodySkeletons()) {
      sk.state.timeScale = ts;
      sk.state.setAnimation(0, CANNON_TUNING.spine.shotClip, false);
      sk.state.addAnimation(0, CANNON_TUNING.spine.loadClip, false, 0);
      sk.state.addAnimation(0, CANNON_TUNING.spine.idleClip, true, 0);
      played = true;
    }
    return played;
  }

  /**
   * Apply a loadout (`[0]` chamber, `[1..]` queue) as a SILENT replace: values swap, no load
   * animation, leftover load choreography neutralised, glint over every seated round. The load clip
   * plays only after a shot (`applyPostFireLoadout`).
   */
  public setLoadout(multipliers: string[]): void {
    this.pendingLoadout = null;
    if (this.loadoutEquals(multipliers)) return;
    this.clearLoadingRound();
    this.applyLoadoutValues(multipliers);
    this.startIdle();
    if (this.queueShine) {
      for (const bullet of this.queueBullets) {
        if (bullet.visible && !bullet.destroyed) this.queueShine(bullet);
      }
    }
  }

  private applyLoadoutValues(multipliers: string[]): void {
    this.lastChamberMultiplier = multipliers[0] ?? "";
    this.appliedLoadout = [...multipliers];
    for (let i = 0; i < this.queueBullets.length; i++) {
      const bullet = this.queueBullets[i]!;
      if (i < multipliers.length) {
        bullet.setMultiplier(this.format(multipliers[i]!));
        bullet.visible = true;
      } else {
        bullet.visible = false;
      }
    }
  }

  /** Stage the post-FIRE loadout: applied when the queued `load` clip STARTS (in sync with the chamber load-in). */
  public applyPostFireLoadout(multipliers: string[]): void {
    if (this.bodySkeletons().length === 0) {
      this.applyLoadoutValues(multipliers);
      return;
    }
    this.pendingLoadout = multipliers;
  }

  /**
   * The chamber round loading in, with the z-order FLIP: a dedicated round rides the chamber
   * follower's world position, IN FRONT of cannon_top for the first `roundLoadBehindFraction` of the
   * load, then BEHIND (a child of the body Spine). The real chamber bullet hides meanwhile.
   * `loadSeconds` is the load clip's own length, taken from its track entry, so the flip follows the art
   * when the clip is retimed.
   */
  private presentRoundLoad(multiplier: string, loadSeconds: number): void {
    const runner = this.runner;
    const slot = this.roundLoadPlaceholder;
    const chamber = this.queueBullets[0];
    const follower = this.queueFollowers[0];
    const behindParent = this.cannonSkeleton;
    if (!runner || !chamber || !follower || !behindParent || !this.createBullet) return;

    // Match the socketed chamber bullet's WORLD scale (queueBulletScale under the ×0.282 body).
    const chamberWorldScale = worldScaleOf(chamber);
    this.clearLoadingRound();
    chamber.visible = false;

    const round = this.createBullet();
    round.setMultiplier(this.format(multiplier));
    // Hidden until its first placement below: this runs from the load clip's `start`, which Spine
    // dispatches BEFORE it applies the new pose, so the follower still holds the shot's last frame —
    // the spot the fired round just left. The tween's first tick places it against a written pose.
    round.visible = false;
    slot.addChild(round);
    placeAtWorld(round, follower, chamberWorldScale);
    this.loadingRound = round;

    const behindAt = CANNON_TUNING.roundLoadBehindFraction;
    let flipped = false;
    void runner
      .tweenProgress(
        loadSeconds,
        (t) => {
          if (round.destroyed) return;
          if (!flipped && t >= behindAt) {
            flipped = true;
            behindParent.addChild(round); // BEHIND cannon_top (a child of the body Spine)
          }
          placeAtWorld(round, follower, chamberWorldScale); // ride the chamber bone each frame
          round.visible = true;
        },
        CANNON_TUNING.roundLoadScope,
        { easing: "linear" },
      )
      .then(() => {
        if (this.loadingRound !== round) return; // superseded by a newer load
        if (!round.destroyed) round.destroy({ children: true });
        this.loadingRound = null;
        if (!chamber.destroyed) chamber.visible = true;
      });
  }

  private loadoutEquals(multipliers: string[]): boolean {
    if (multipliers.length !== this.appliedLoadout.length) return false;
    for (let i = 0; i < multipliers.length; i++) if (multipliers[i] !== this.appliedLoadout[i]) return false;
    return true;
  }

  private clearLoadingRound(): void {
    if (this.loadingRound && !this.loadingRound.destroyed) this.loadingRound.destroy({ children: true });
    this.loadingRound = null;
  }

  /**
   * Fire the cannon: `shot` → `load` → `idle` on both layers; `onImpact` fires ON the shot's
   * `explotion` event (this game fires at the blast — no projectile). Resolves at the burst.
   */
  public async playFire(_firedMultiplier: string, onImpact?: () => void | Promise<void>): Promise<void> {
    const runner = this.runner;
    if (!runner) {
      await onImpact?.();
      return;
    }
    const firedSpine = this.playShotThenReload();
    if (firedSpine) await this.waitForBlast(runner);
    await onImpact?.();
  }

  /** The blast: the shot clip's event, its interrupt or its end, whichever comes first; the timeout is the net. */
  private async waitForBlast(runner: PresentationTaskRunner): Promise<void> {
    const blast = new Promise<void>((resolve) => {
      this.resolveFireImpact = resolve;
    });
    try {
      await Promise.race([blast, runner.sleep(this.blastTimeoutSeconds(), CANNON_TUNING.fireScope)]);
    } finally {
      this.resolveFireImpact = null;
    }
  }

  /**
   * The fallback wait, from the shot clip itself: the fire event's authored time (or the clip's length when
   * the clip has no such event) with a safety margin, so the live event wins the race in normal play.
   */
  private blastTimeoutSeconds(): number {
    if (this.fireImpactTimeout !== null) return this.fireImpactTimeout;
    const animation = this.cannonSkeleton?.skeleton.data.findAnimation(CANNON_TUNING.spine.shotClip) ?? null;
    let seconds = animation?.duration ?? 0;
    for (const timeline of animation?.timelines ?? []) {
      if (!(timeline instanceof EventTimeline)) continue;
      const fireEvent = timeline.events.find((e) => e.data.name === CANNON_TUNING.spine.fireEventName);
      if (fireEvent) {
        seconds = fireEvent.time;
        break;
      }
    }
    this.fireImpactTimeout = seconds * FIRE_IMPACT_TIMEOUT_SAFETY;
    return this.fireImpactTimeout;
  }

  private format(value: string): string {
    return `${CANNON_TUNING.labelPrefix}${value}${CANNON_TUNING.labelSuffix}`;
  }
}

/** Uniform world scale of a container (x axis length of its world matrix). */
function worldScaleOf(node: Container): number {
  const m = node.worldTransform;
  return Math.hypot(m.a, m.b) || 1;
}

/** Put `node` at `anchor`'s world position with the given world scale, whatever its parent. */
function placeAtWorld(node: Container, anchor: Container, worldScale: number): void {
  const parent = node.parent;
  if (!parent) return;
  const global = anchor.getGlobalPosition();
  node.position.copyFrom(parent.toLocal(global));
  const parentScale = worldScaleOf(parent);
  node.scale.set(worldScale / parentScale);
}
